import type FormPage from '@/modules/common/common/models/APP/FormPage'
import type Insured from '@/modules/common/common/models/APP/Insured'
import BankAccountForm from '@/modules/common/hiring/views/forms/BankAccountForm.vue'
import InsuranceAnotherCompanyAttachFileForm from '@/modules/common/hiring/views/forms/InsuranceAnotherCompanyAttachFileForm.vue'
import InsuranceAnotherCompanyDataForm from '@/modules/common/hiring/views/forms/InsuranceAnotherCompanyDataForm.vue'
import InsuranceBodyMeasurementsForm from '@/modules/common/hiring/views/forms/InsuranceBodyMeasurementsForm.vue'
import InsurancePersonalDataStep1Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep1Form.vue'
import InsurancePersonalDataStep2Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep2Form.vue'
import InsurancePersonalDataStep3Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep3Form.vue'
import InsurancePersonalDataStep4Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep4Form.vue'
import InsurancePersonalDataStep5Form from '@/modules/common/hiring/views/forms/InsurancePersonalDataStep5Form.vue'
import { UserRoleEnum } from '@/modules/core/models/enums'
import LifeInsuranceProcessStepEnum from '@/modules/life/hiring/models/APP/LifeInsuranceProcessStepEnum'
import BeneficiaryForm from '@/modules/life/hiring/views/forms/BeneficiaryForm.vue'
import BeneficiaryTypeForm from '@/modules/life/hiring/views/forms/BeneficiaryTypeForm.vue'
import HasMortgageForm from '@/modules/life/hiring/views/forms/HasMortgageForm.vue'
import MortgageForm from '@/modules/life/hiring/views/forms/MortgageForm.vue'
import ProfessionAndActivityForm from '@/modules/life/hiring/views/forms/ProfessionAndActivityForm.vue'
import Step0Form from '@/modules/life/hiring/views/forms/Step0Form.vue'
import { isBigger } from '@/utils/date.utils'
import BeneficiariesForm from '../views/forms/BeneficiariesForm.vue'
import MedicalQuestionnaireForm from '../views/forms/MedicalQuestionnaireForm.vue'
import CallMeView from '../views/CallMeView.vue'

const validationGuards = {
  isOfAge: (insured: Insured) => {
    if (insured.role !== UserRoleEnum.INSURED) {
      return true
    }

    const birthDate = insured.userDetails.birthDate
    if (!birthDate) {
      return true
    }

    return isBigger(birthDate, 18)
  }
}

export const STEP0_PAGES: Record<string, FormPage> = {
  Step0_1_StartDateForm: {
    form: Step0Form,
    next: ['Step1_1_ProfessionAndActivityForm'],
    extra: {
      step: 0,
      summaryStep: 0,
      showActions: false,
      processStep: [LifeInsuranceProcessStepEnum.RATER]
    }
  }
}

export const STEP1_PAGES: Record<string, FormPage> = {
  Step1_1_ProfessionAndActivityForm: {
    form: ProfessionAndActivityForm,
    next: ['Step2_1_InsuranceAnotherCompanyDataForm'],
    extra: {
      step: 1,
      summaryStep: 0,
      showActions: false,
      processStep: [LifeInsuranceProcessStepEnum.DOCUMENTS]
    }
  }
}
export const STEP2_PAGES: Record<string, FormPage> = {
  Step2_1_InsuranceAnotherCompanyDataForm: {
    form: InsuranceAnotherCompanyDataForm,
    next: ['Step2_2_InsuranceAnotherCompanyAttachFileForm'],
    extra: {
      showDeclaration: true,
      step: 2,
      summaryStep: 1,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.INSURED],
      processStep: [LifeInsuranceProcessStepEnum.DOCUMENTS, LifeInsuranceProcessStepEnum.INSUREDS],
      summarySubStepIndex: 0
    }
  },
  Step2_2_InsuranceAnotherCompanyAttachFileForm: {
    form: InsuranceAnotherCompanyAttachFileForm,
    next: ['Step2_3_InsurancePersonalDataStep1Form'],
    extra: {
      step: 2,
      summaryStep: 2,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED],
      onSubmit: 'sendPolicyHolderDocument',
      summarySubStepIndex: 0
    }
  },
  Step2_3_InsurancePersonalDataStep1Form: {
    form: InsurancePersonalDataStep1Form,
    next: ['Step2_4_InsurancePersonalDataStep2Form'],
    extra: {
      step: 2,
      summaryStep: 2,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      processStep: [LifeInsuranceProcessStepEnum.HOLDER],
      summarySubStepIndex: 1
    }
  },
  Step2_4_InsurancePersonalDataStep2Form: {
    form: InsurancePersonalDataStep2Form,
    next: ['Step2_5_InsurancePersonalDataStep3Form'],
    extra: {
      step: 2,
      summaryStep: 2,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      summarySubStepIndex: 2
    }
  },
  Step2_5_InsurancePersonalDataStep3Form: {
    form: InsurancePersonalDataStep3Form,
    next: ['Step2_6_InsurancePersonalDataStep4Form'],
    extra: {
      showActions: false,
      step: 2,
      summaryStep: 3,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      onSubmit: 'sendYoungInsured', //? only for young insureds
      summarySubStepIndex: 2
    }
  },
  Step2_6_InsurancePersonalDataStep4Form: {
    form: InsurancePersonalDataStep4Form,
    next: ['Step2_7_InsurancePersonalDataStep5Form'],
    extra: {
      step: 2,
      summaryStep: 2,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.INSURED],
      guards: [validationGuards.isOfAge], //? skip for young insureds
      onSubmit: 'sendInsureds',
      summarySubStepIndex: 3
    }
  },
  Step2_7_InsurancePersonalDataStep5Form: {
    form: InsurancePersonalDataStep5Form,
    next: ['Step3_1_InsuranceBodyMeasurementsForm'],
    extra: {
      step: 2,
      summaryStep: 2,
      hideInsuredSubtitle: true,
      roles: [UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED],
      onSubmit: 'sendPolicyHolder',
      summarySubStepIndex: 3
    }
  }
}

export const STEP3_PAGES: Record<string, FormPage> = {
  Step3_1_InsuranceBodyMeasurementsForm: {
    form: InsuranceBodyMeasurementsForm,
    next: ['Step3_2_MedicalQuestionnaireForm'],
    extra: {
      step: 3,
      summaryStep: 3,
      processStep: [LifeInsuranceProcessStepEnum.QUESTIONNAIRES],
      hideInsuredSubtitle: true
    }
  },
  Step3_2_MedicalQuestionnaireForm: {
    form: MedicalQuestionnaireForm,
    next: ['Step4_1_HasMortgageForm'],
    extra: {
      step: 3,
      summaryStep: 3,
      onSubmit: 'sendQuestionnaire',
      hideInsuredSubtitle: true
    }
  }
}

export const STEP4_PAGES: Record<string, FormPage> = {
  Step4_1_HasMortgageForm: {
    form: HasMortgageForm,
    next: ['Step4_3_BeneficiaryTypeForm', 'Step4_2_MortgageForm'],
    extra: {
      step: 4,
      summaryStep: 4,
      processStep: [LifeInsuranceProcessStepEnum.BENEFICIARIES],
      hideInsuredSubtitle: true
    }
  },
  Step4_2_MortgageForm: {
    form: MortgageForm,
    next: ['Step4_3_BeneficiaryTypeForm'],
    extra: {
      step: 4,
      summaryStep: 4,
      processStep: [LifeInsuranceProcessStepEnum.BENEFICIARIES],
      hideInsuredSubtitle: true
    }
  },
  Step4_3_BeneficiaryTypeForm: {
    form: BeneficiaryTypeForm,
    next: ['Step5_1_BankAccountForm', 'Step4_4_BeneficiaryForm'],
    extra: {
      step: 4,
      summaryStep: 5,
      processStep: [LifeInsuranceProcessStepEnum.BENEFICIARIES],
      onSubmit: 'sendBeneficiaries',
      hideInsuredSubtitle: true
    }
  },
  Step4_4_BeneficiaryForm: {
    form: BeneficiaryForm,
    next: ['Step4_5_BeneficiariesForm'],
    extra: {
      step: 4,
      summaryStep: 5,
      hideInsuredSubtitle: true
    }
  },
  Step4_5_BeneficiariesForm: {
    form: BeneficiariesForm,
    next: ['Step5_1_BankAccountForm'],
    extra: {
      step: 4,
      summaryStep: 5,
      onSubmit: 'sendBeneficiaries',
      hideInsuredSubtitle: true
    }
  }
}
export const STEP5_PAGES: Record<string, FormPage> = {
  Step5_1_BankAccountForm: {
    form: BankAccountForm,
    next: [],
    extra: {
      step: 5,
      summaryStep: 6,
      onSubmit: 'sendPayment',
      processStep: [LifeInsuranceProcessStepEnum.PAYMENT, LifeInsuranceProcessStepEnum.EMISSION],
      hideInsuredSubtitle: true
    }
  }
}

export const CALL_ME_PAGES: Record<string, FormPage> = {
  CallMeView: {
    form: CallMeView,
    next: [],
    extra: {
      showActions: false,
      hideInsuredSubtitle: true,
      showSummary: false,
      showProgress: false
    }
  }
}

export default {
  STEP0_PAGES,
  STEP1_PAGES,
  STEP2_PAGES,
  STEP3_PAGES,
  STEP4_PAGES,
  STEP5_PAGES,
  CALL_ME_PAGES
}
